import {isExternalLink} from '../../utils/utils'
import {sanitizeAnchorTarget} from '../utils/utils'
import useNavigation from '../../hooks/use-navigation'
import {convertToRelativePath} from '../../utils/url'
import {useMemo} from 'react'
import useLeapOverlay from './useOverlay'
import {trackCtaClick} from '../../tracking/analytics'
import {useContentDrawer} from '../components/content-drawer'

const useCTAAction = ({action, openInNewTab = false, onClick: _onClick, trackingContext} = {}) => {
    // use relative links instead for DEV/STG/PRD to handle mobify and local development environments

    const {onOpen: onOverlayOpen} = useLeapOverlay()
    const {open: openContentDrawer} = useContentDrawer()

    const linkHref = useMemo(() => {
        switch (action?.type) {
            case 'overlay':
            case 'sidebar':
                return undefined
            case 'anchor':
                return sanitizeAnchorTarget(action.value)
            default:
                return convertToRelativePath(action?.value)
        }
    }, [action?.type, action?.value])
    const navigate = useNavigation()

    const {moduleName, eventLabel = 'N/A'} = trackingContext || {}

    const onClick = (e) => {
        if (trackingContext) {
            let moduleLink = linkHref || 'N/A'

            if (action?.type === 'overlay' && action?.value) {
                moduleLink = action.value
            }

            trackCtaClick({
                moduleName,
                moduleElement: 'cta',
                eventAction: 'cta click',
                eventLabel,
                moduleLink
            })
        }

        // Call any provided onClick handler first
        if (_onClick) {
            _onClick(e)
            // and short-circuit if event was handled/prevented
            if (e.defaultPrevented) {
                return
            }
        }

        switch (action?.type) {
            case 'overlay': {
                e.preventDefault()
                onOverlayOpen(action.value)
                return
            }
            case 'sidebar': {
                e.preventDefault()
                openContentDrawer({contentId: action.value})
                return
            }
        }

        if (!linkHref) {
            e.preventDefault()
            return
        }

        // Handle different action types
        switch (action.type) {
            case 'url':
            case 'anchor':
                // For url and anchor types, use default browser handling
                return

            case 'product':
            case 'category':
            case 'content': {
                if (isExternalLink(linkHref)) {
                    // For external links, fallback to default browser handling
                    return
                }

                if (!openInNewTab) {
                    e.preventDefault()
                    // For internal links, use client-side navigation
                    navigate(linkHref)
                }
                return
            }

            default:
                // Fallback: leave default handling for unknown types
                return
        }
    }

    const target = openInNewTab ? '_blank' : undefined
    const rel = openInNewTab ? 'noopener noreferrer' : undefined

    return linkHref
        ? {
              href: linkHref,
              onClick,
              target,
              rel,
              as: 'a'
          }
        : {
              onClick,
              as: 'button'
          }
}

export default useCTAAction
